import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Download, Trash2, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../lib/api';
import ClientProfileModal from '../components/ClientProfileModal';
import RGPDModal from '../components/RGPDModal';

const ClientesPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [profileClient, setProfileClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', dni: '', notes: '', date_of_birth: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const fileInputRef = useRef(null);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      const sorted = response.data.sort((a, b) => {
        const dateA = a.registration_date ? new Date(a.registration_date) : new Date(0);
        const dateB = b.registration_date ? new Date(b.registration_date) : new Date(0);
        return dateB - dateA;
      });
      setClients(sorted);
    } catch (error) {
      console.error("Error fetching clients", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcelClients = () => {
    const dataObj = clients.map(c => ({
      'Nombre': c.name || '',
      'Email': c.email || '',
      'Teléfono': c.phone || '',
      'DNI': c.dni || '',
      'Fototipo': c.skin_type || '',
      'Fecha Alta': c.registration_date ? c.registration_date.split('-').reverse().join('/') : '',
      'Fecha Nacimiento': c.date_of_birth ? c.date_of_birth.split('-').reverse().join('/') : '',
      'Minutos Disponibles': c.remaining_minutes || 0,
      'Firma RGPD': c.rgpd_status || 'Pendiente',
      'Observaciones': c.notes || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataObj);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");
    XLSX.writeFile(workbook, "Clientes_SolPlanet.xlsx");
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (profileClient) {
      const updated = clients.find(c => c.id === profileClient.id);
      if (updated && updated.remaining_minutes !== profileClient.remaining_minutes) {
        setProfileClient(updated);
      }
    }
  }, [clients, profileClient]);

  const openSignatureModal = (client) => {
    setSelectedClient(client);
    setShowSignatureModal(true);
  };

  const closeSignatureModal = () => {
    setShowSignatureModal(false);
    setSelectedClient(null);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.name) return;
    try {
      const response = await api.post('/clients', {
        ...newClient,
        rgpd_status: 'Pendiente',
        remaining_minutes: 0,
        active: true
      });
      setClients([response.data, ...clients]);
      setShowCreateModal(false);
      setNewClient({ name: '', email: '', phone: '', dni: '', notes: '', date_of_birth: '' });
    } catch (error) {
      console.error("Error creating client", error);
    }
  };

  const handleDeleteClient = async (e, clientId) => {
    e.stopPropagation();
    if (!window.confirm("¿Seguro que deseas eliminar el cliente permanentemente? Sus sesiones y bonos previos NO se borrarán para mantener el histórico contable.")) return;
    
    try {
      await api.delete(`/clients/${clientId}`);
      setClients(clients.filter(c => c.id !== clientId));
      if (profileClient && profileClient.id === clientId) {
        setProfileClient(null);
      }
    } catch (error) {
      alert("Error al eliminar el cliente.");
      console.error(error);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm("⚠️ ALERTA ⚠️: ¿Estás ABSOLUTAMENTE SEGURO de que deseas BORRAR TODOS los clientes existentes de la base de datos y sustituirlos en bloque por el archivo que estás subiendo? Esta acción no se puede deshacer.")) {
        e.target.value = null;
        return;
    }
    
    setLoading(true);
    try {
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'array' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                // Read as array of arrays
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                
                if (data.length <= 1) {
                    alert("El archivo Excel parece estar vacío o no pudo leerse correctamente.");
                    setLoading(false);
                    return;
                }

                await api.delete('/clients/action/wipe_all');
                
                // Send the raw parsed data to the backend where the intelligent heuristics live
                const response = await api.post('/clients/import_bulk_json', { rows: data });
                
                fetchClients();
                alert(`Importación completada. Se importaron ${response.data.imported} clientes correctamente.`);
            } catch (err) {
                alert("Error parseando o importando archivo: " + (err.response?.data?.detail || err.message || "Error desconocido"));
                console.error(err);
                setLoading(false);
            }
        };
        reader.readAsArrayBuffer(file);
    } catch(err) {
        alert("Error crítico iniciando importación: " + (err.response?.data?.detail || err.message));
        console.error(err);
        setLoading(false);
    }
    e.target.value = null;
  };

  const filteredClients = clients.filter(client => {
    const q = searchQuery.toLowerCase();
    const matchName = client.name?.toLowerCase().includes(q);
    const matchEmail = client.email?.toLowerCase().includes(q);
    return matchName || matchEmail;
  });

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const paginatedClients = filteredClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-stone-800">Clientes</h1>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar cliente o email..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 w-full sm:w-64"
            />
          </div>
          <input 
            type="file" 
            accept=".xlsx" 
            ref={fileInputRef} 
            onChange={handleImportExcel} 
            className="hidden" 
          />
          <button 
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Sustituir toda la tabla de Excel"
          >
            <Upload size={18} />
            Importar
          </button>
          <button 
            onClick={exportToExcelClients}
            className="flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Descargar listado en Excel (XLSX)"
          >
            <Download size={18} />
            Excel
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-stone-900 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
          >
            Nuevo Cliente
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-500">Cargando clientes...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 text-stone-500 border-b border-stone-100">
                  <th className="px-6 py-4 font-medium">Cliente</th>
                  <th className="px-6 py-4 font-medium">DNI</th>
                  <th className="px-6 py-4 font-medium">Teléfono</th>
                  <th className="px-6 py-4 font-medium">Observaciones</th>
                  <th className="px-6 py-4 font-medium">Tiempo disponible</th>
                  <th className="px-6 py-4 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((client) => (
                  <tr 
                    key={client.id} 
                    onClick={() => setProfileClient(client)}
                    className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-stone-800">{client.name}</div>
                    </td>
                    <td className="px-6 py-4 text-stone-600">
                      {client.dni || '-'}
                    </td>
                    <td className="px-6 py-4 text-stone-600">
                      {client.phone || '-'}
                    </td>
                    <td className="px-6 py-4 text-stone-600 text-sm max-w-xs truncate" title={client.notes || ''}>
                      {client.notes || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                        {client.remaining_minutes} min
                      </span>
                    </td>
                    <td className="px-6 py-4 flex items-center gap-3">
                      {client.rgpd_status === 'Acepto' ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            openSignatureModal(client);
                          }}
                          className="text-teal-600 hover:text-teal-700 text-sm font-medium transition"
                        >
                          RGPD Firmado
                        </button>
                      ) : (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            openSignatureModal(client);
                          }}
                          className="text-stone-500 hover:text-stone-700 font-medium text-sm transition pb-0.5 border-b border-stone-300 border-dashed"
                        >
                          Firma RGPD
                        </button>
                      )}
                      
                      <button 
                        onClick={(e) => handleDeleteClient(e, client.id)}
                        className="p-1.5 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-auto"
                        title="Eliminar cliente"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredClients.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-stone-500">
                      {searchQuery ? "No se encontraron clientes para tu búsqueda." : "No hay clientes registrados."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-stone-100 bg-stone-50/50 gap-4">
                <div className="text-sm text-stone-500">
                  Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredClients.length)}</span> de <span className="font-medium">{filteredClients.length}</span> resultados
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm text-stone-600 font-medium px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RGPD Modal */}
      {showSignatureModal && (
        <RGPDModal 
          client={selectedClient} 
          onClose={closeSignatureModal} 
          onUpdate={fetchClients} 
        />
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-semibold text-stone-800">Nuevo Cliente</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-stone-400 hover:text-stone-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateClient} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nombre Completo *</label>
                <input 
                  type="text" 
                  value={newClient.name} 
                  onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                <input 
                  type="email" 
                  value={newClient.email} 
                  onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Teléfono</label>
                <input 
                  type="tel" 
                  value={newClient.phone} 
                  onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">DNI</label>
                <input 
                  type="text" 
                  value={newClient.dni} 
                  onChange={(e) => setNewClient({...newClient, dni: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Fecha de Nacimiento</label>
                <input 
                  type="date" 
                  value={newClient.date_of_birth} 
                  onChange={(e) => setNewClient({...newClient, date_of_birth: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Observaciones</label>
                <textarea 
                  rows="3"
                  value={newClient.notes} 
                  onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  placeholder="Detalles médicos, preferencias..."
                />
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-stone-500 hover:text-stone-700 font-medium"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="bg-amber-500 text-stone-900 px-4 py-2 rounded-xl font-medium hover:bg-amber-600 transition"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Profile Modal */}
      {profileClient && (
        <ClientProfileModal 
          client={profileClient} 
          onClose={() => setProfileClient(null)} 
          onUpdate={fetchClients} 
        />
      )}
    </div>
  );
};

export default ClientesPage;
